import {
  dayFmt,
  monthYearFromDates,
  fallbackRangeLabel,
  buildEmptySeriesForRange,
} from './meterDetails.helpers';

export function computeEffectiveDateRange(dateRange, startDate, endDate, localFallback) {
  if (startDate && endDate) return { start: startDate, end: endDate };
  const hasExplicitRange = dateRange && (dateRange.start || dateRange.end);
  return hasExplicitRange ? dateRange : (localFallback ?? undefined);
}

export function pickBaseSeries(fetchedData, propData, effectiveRange, meterType) {
  const fromHook = Array.isArray(fetchedData) && fetchedData.length ? fetchedData : null;
  const fromProp = !fromHook && Array.isArray(propData) && propData.length ? propData : null;
  if (fromHook) return fromHook;
  if (fromProp) return fromProp;
  return buildEmptySeriesForRange(effectiveRange, meterType);
}

export function parseDatesFromSeries(series = [], key = 'label') {
  return (Array.isArray(series) ? series : [])
    .map((r) => {
      const v = typeof key === 'function' ? key(r) : (key in (r || {}) ? r[key] : r?.label);
      const d = v ? new Date(v) : null;
      return d && !isNaN(d) ? d : null;
    })
    .filter(Boolean);
}

export function makeChartData(series = [], meterType) {
  if (!Array.isArray(series) || series.length === 0) {
    return [{ day: '', fullDate: '', value: null, ac: null, dc: null }];
  }
  return series.map((r) => {
    const d = r?.label ? new Date(r.label) : (r?.fullDate ? new Date(r.fullDate) : null);
    const day = d && !isNaN(d) ? dayFmt.format(d) : (r?.day ?? '');
    const fullDate = d && !isNaN(d) ? d.toLocaleDateString('en-GB') : (r?.fullDate ?? '');
    return meterType === 3
      ? { day, fullDate, ac: r.ac ?? null, dc: r.dc ?? null }
      : { day, fullDate, value: r.value ?? null };
  });
}

export function buildYAxisDynamic(values = [], stepMin = 250, stepMax = 2000) {
  const maxVal = Math.max(0, ...values.map((v) => (Number.isFinite(v) ? v : 0)));
  if (maxVal === 0) {
    return { yMax: 1, ticks: [0, 1] };
  }

  const niceStep = (min, max) => {
    const bases = [1, 2, 2.5, 5, 10];
    const rough = Math.max(min, max / 4);
    const pow = Math.pow(10, Math.floor(Math.log10(rough)));
    let best = bases[0] * pow;
    for (const b of bases) {
      const candidate = b * pow;
      if (candidate >= rough) {
        best = candidate;
        break;
      }
    }
    if (best < min) best = min;
    if (best > max) best = max;
    return best;
  };

  const step = niceStep(stepMin, stepMax);
  const yMax = Math.ceil(maxVal / step) * step;
  const ticks = [];
  for (let t = 0; t <= yMax; t += step) ticks.push(t);
  if (ticks[ticks.length - 1] !== yMax) ticks.push(yMax);

  return { yMax, ticks };
}

export function computeYAxisWidth(ticks = []) {
  const labels = (ticks && ticks.length ? ticks : [0]).map((t) =>
    Number.isFinite(t) ? t.toLocaleString('en-GB') : String(t)
  );
  const maxChars = Math.max(...labels.map((s) => s.length), 3);
  const charPx = 7.2;
  const pad = 18;
  const base = 44;
  return Math.max(base, Math.ceil(maxChars * charPx) + pad);
}

export function computeMonthYearLabel(parsedDates = [], effectiveRange) {
  const lbl = monthYearFromDates(parsedDates);
  return lbl || fallbackRangeLabel(effectiveRange);
}
